import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import { useResponsivePopupOptions } from '../shell/useResponsivePopup';
import { listItems, type BrowseItem } from './fileBrowserApi';
import {
  destinationMountDisabledReason,
  preferredDestinationMount,
  transferSelectionLabel
} from './fileBrowserDestination';
import type { MountRoute } from './createFileBrowserProvider';
import '../documentEditor/DocumentOpenPickerPopup.css';
import './FileBrowserDestinationPickerPopup.css';

export type FileBrowserDestinationPickerPopupProps = {
  visible: boolean;
  operation: 'Copy' | 'Move';
  itemNames: readonly string[];
  mounts: readonly MountRoute[];
  initialMountId?: string | null;
  initialFolderPath?: string;
  onClose: () => void;
  onSelect: (mount: MountRoute, folderPath: string) => void;
};

type Crumb = { label: string; path: string };

export function FileBrowserDestinationPickerPopup({
  visible,
  operation,
  itemNames,
  mounts,
  initialMountId,
  initialFolderPath = '',
  onClose,
  onSelect
}: FileBrowserDestinationPickerPopupProps) {
  const popupOptions = useResponsivePopupOptions({ width: 860, height: 620 });
  const [mountId, setMountId] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState('');
  const [items, setItems] = useState<BrowseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedMount = mounts.find((mount) => mount.id === mountId) ?? null;
  const verb = operation === 'Move' ? 'Move' : 'Copy';
  const summaryPrefix = operation === 'Move' ? 'Moving' : 'Copying';
  const summary = transferSelectionLabel(itemNames);

  const crumbs = useMemo((): Crumb[] => {
    if (!selectedMount) return [];
    const parts: Crumb[] = [{ label: selectedMount.key, path: '' }];
    if (!folderPath) return parts;
    const segments = folderPath.split('/').filter(Boolean);
    let accum = '';
    for (const segment of segments) {
      accum = accum ? `${accum}/${segment}` : segment;
      parts.push({ label: segment, path: accum });
    }
    return parts;
  }, [folderPath, selectedMount]);

  const locationLabel = crumbs.map((crumb) => crumb.label).join(' / ');

  const loadFolder = useCallback(async (nextMount: MountRoute, nextPath: string) => {
    setLoading(true);
    setError(null);
    try {
      const next = await listItems(nextMount.id, nextPath || undefined);
      const folders = next
        .filter((item) => item.isDirectory)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      setItems(folders);
      setMountId(nextMount.id);
      setFolderPath(nextPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not list folder.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    const preferred = preferredDestinationMount(mounts, initialMountId);
    if (!preferred) {
      setMountId(null);
      setFolderPath('');
      setItems([]);
      setError('There is no writable storage location to copy or move into.');
      return;
    }
    const startPath = preferred.id === initialMountId ? initialFolderPath : '';
    void loadFolder(preferred, startPath.replace(/\\/g, '/').trim().replace(/^\/+|\/+$/g, ''));
  }, [visible, initialMountId, initialFolderPath, loadFolder, mounts]);

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      showTitle
      title={`${verb} to`}
      showCloseButton
      dragEnabled={!popupOptions.fullScreen}
      hideOnOutsideClick={false}
      {...popupOptions}
      wrapperAttr={{ class: 'jarvis-doc-open-picker-popup' }}
      position={{ my: 'center', at: 'center', of: window }}
    >
      <div className="jarvis-dest-picker jarvis-doc-open-picker">
        <p className="jarvis-dest-picker__summary">
          {summaryPrefix} <strong>{summary}</strong>
        </p>

        <div className="jarvis-dest-picker__mounts" role="group" aria-label="Storage locations">
          {mounts.map((mount) => {
            const reason = destinationMountDisabledReason(mount);
            const disabled = reason != null;
            return (
              <button
                key={mount.id}
                type="button"
                className={`jarvis-dest-picker__mount${mount.id === mountId ? ' jarvis-dest-picker__mount--active' : ''}`}
                disabled={disabled}
                title={reason ?? mount.key}
                onClick={() => void loadFolder(mount, '')}
              >
                {mount.key}
              </button>
            );
          })}
        </div>

        {selectedMount ? (
          <div className="jarvis-doc-open-picker__toolbar">
            <nav className="jarvis-doc-open-picker__crumbs" aria-label="Folder path">
              {crumbs.map((crumb, index) => (
                <span key={`${crumb.label}-${index}`}>
                  {index > 0 ? <span className="jarvis-doc-open-picker__crumb-sep">/</span> : null}
                  <button
                    type="button"
                    className="jarvis-doc-open-picker__crumb"
                    disabled={index === crumbs.length - 1}
                    onClick={() => void loadFolder(selectedMount, crumb.path)}
                  >
                    {crumb.label}
                  </button>
                </span>
              ))}
            </nav>
          </div>
        ) : null}

        {error ? <p className="jarvis-doc-open-picker__error">{error}</p> : null}

        <div className="jarvis-doc-open-picker__list" role="listbox" aria-label="Folders">
          {loading ? <p className="jarvis-doc-open-picker__muted">Loading…</p> : null}
          {!loading
            ? items.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  role="option"
                  className="jarvis-doc-open-picker__row"
                  onClick={() => selectedMount && void loadFolder(selectedMount, item.path)}
                >
                  <span className="jarvis-doc-open-picker__row-icon dx-icon dx-icon-folder" />
                  <span className="jarvis-doc-open-picker__row-name">{item.name}</span>
                </button>
              ))
            : null}
          {!loading && !error && selectedMount && items.length === 0 ? (
            <p className="jarvis-doc-open-picker__empty">
              No subfolders here. You can still {verb.toLowerCase()} into this folder.
            </p>
          ) : null}
        </div>

        {selectedMount ? (
          <p className="jarvis-dest-picker__here">
            {verb} into <strong>{locationLabel || selectedMount.key}</strong>
          </p>
        ) : null}

        <div className="jarvis-doc-open-picker__footer">
          <Button text="Cancel" stylingMode="outlined" onClick={onClose} />
          <Button
            text={`${verb} here`}
            type="default"
            disabled={!selectedMount}
            onClick={() => selectedMount && onSelect(selectedMount, folderPath)}
          />
        </div>
      </div>
    </Popup>
  );
}
